import {EditIcon} from "@chakra-ui/icons";
import {
  Button,
  Checkbox,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Stack,
  Td,
  Text,
  Textarea,
  Tr,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import React, {useState} from "react";
import {useMutation, useQueryClient} from "react-query";

import {postCandidateSetActive, postCandidatesSetActive, updateCandidate} from "../../shared/Api";
import {errorToast, successToast} from "../../shared/helper/ToastHelper";
import {useAuthentication} from "../../shared/hooks/AuthenticationProvider";
import {Authority, CandidateStatus, EvaluationType} from "../../shared/model/Constants";
import {CandidateDataDto, CandidateSummaryDto} from "../../shared/model/Dto";
import {adminKeys} from "../../shared/QueryKeys";
import {displayCandidateStatus, displayTime, parseDateTime} from "../../shared/Utils";
import DownloadCandidateCertificate from "./DownloadCandidateCertificate";

type CandidateSummaryItemProps = {
  candidateSummary: CandidateSummaryDto;
};
const CandidateSummaryItem = (props: CandidateSummaryItemProps) => {
  const authentication = useAuthentication();
  const isAdmin =
    authentication?.currentUserQuery?.data?.data?.authorities?.includes(Authority.ROLE_ADMIN);
  const isTeacher =
    authentication?.currentUserQuery?.data?.data?.authorities?.includes(Authority.ROLE_TEACHER);
  const isPublic = !isAdmin && !isTeacher;

  const urlParams = new URLSearchParams(window.location.search);
  const edit = urlParams.get('toggle');
  const activateCandidates = edit === 'c' || !isPublic;

  const stackSpace= "10px";
  const stackPad= "8px";
  const maxTime = 18000;

  const toast = useToast();
  const queryClient = useQueryClient();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [currentCandidate, setCurrentCandidate] = useState<CandidateDataDto>();

  const [testValue, setTestValue] = useState<string>("");
  const [levelValue, setLevelValue] = useState<string>("");
  const [statusValue, setStatusValue] = useState<string>("");
  const [activeValue, setActiveValue] = useState<boolean>(false);
  const [enabledFromValue, setEnabledFromValue] = useState<string>("");
  const [enabledToValue, setEnabledToValue] = useState<string>("");
  const [startTimeValue, setStartTimeValue] = useState<string>("");
  const [endTimeValue, setEndTimeValue] = useState<string>("");

  const [questionLimitValue, setQuestionLimitValue] = useState<number>(0);
  const [multipleAttemptsValue, setMultipleAttemptsValue] = useState<number>(0);
  const [timeLimitValue, setTimeLimitValue] = useState<number>(0);
  const [timeLeftValue, setTimeLeftValue] = useState<number>(0);
  const [conditionsValue, setConditionsValue] = useState<string>("");

  const [reportValue, setReportValue] = useState<string>("");
  const [overallValue, setOverallValue] = useState<string>("");

  const updateCandidateMutation = useMutation(({
                                                 username,
                                                 originalTest, token,
                                                 evaluationType, newTest,
                                                 level,
                                                 status, active,
                                                 enabledFrom, enabledTo,
                                                 startTime, endTime,
                                                 multipleAttempts, questionLimit,
                                                 timeLimit, timeLeft,
                                                 conditions,
                                                 report, overall
                                               }: any) =>
      updateCandidate(username, originalTest, token, evaluationType, newTest, level, status, active,
        enabledFrom, enabledTo, startTime, endTime, multipleAttempts, questionLimit, timeLimit, timeLeft,
        conditions, report, overall),
    {
      onSuccess: () => {
        if (currentCandidate) {
          currentCandidate.test = testValue;
          currentCandidate.level = levelValue;
          currentCandidate.status = CandidateStatus[statusValue as keyof typeof CandidateStatus];
          currentCandidate.active = activeValue;
          currentCandidate.enabledFrom = enabledFromValue;
          currentCandidate.enabledTo = enabledToValue;
          currentCandidate.startTime = startTimeValue;
          currentCandidate.endTime = endTimeValue;
          currentCandidate.multipleTries = multipleAttemptsValue;
          currentCandidate.questionLimit = questionLimitValue;
          currentCandidate.timeLimit = timeLimitValue;
          currentCandidate.timeLeft = timeLeftValue;
          currentCandidate.conditions = conditionsValue;
          currentCandidate.reportUrl = reportValue;
          currentCandidate.overall = overallValue;
        }
        successToast(toast, "Candidato actualizado");
        return queryClient.invalidateQueries(adminKeys.candidates());
      },
      onError: () => {
        errorToast(toast, "Error actualizando candidato");
      },
    });

  function handleSaveClick() {
    updateCandidateMutation.mutate({
      username: props.candidateSummary.username,
      originalTest: currentCandidate?.test,
      token: currentCandidate?.token,
      evaluationType: currentCandidate?.evaluationType,

      newTest: testValue != null ? testValue.trim() : null,
      level: levelValue != null ? levelValue.trim() : null,
      status: statusValue,
      active: activeValue,
      enabledFrom: enabledFromValue,
      enabledTo: enabledToValue,
      startTime: startTimeValue,
      endTime: endTimeValue,

      multipleAttempts: multipleAttemptsValue,

      questionLimit: questionLimitValue,
      timeLimit: timeLimitValue,
      timeLeft: timeLeftValue,
      conditions: conditionsValue != null ? conditionsValue.trim() : null,

      report: reportValue != null ? reportValue.trim() : null,
      overall: overallValue != null ? overallValue.trim() : null
    });

    onClose();
  }

  function setCandidateValues(candidate: CandidateDataDto) {
    setCurrentCandidate(candidate);

    setTestValue(candidate.test || "");
    setEnabledFromValue(candidate.enabledFrom);
    setEnabledToValue(candidate.enabledTo);
    setStartTimeValue(candidate.startTime);
    setEndTimeValue(candidate.endTime);
    setStatusValue(candidate.status);
    setLevelValue(candidate.level);
    setActiveValue(candidate.active);
    setMultipleAttemptsValue(candidate.multipleTries);

    setQuestionLimitValue(candidate.questionLimit);
    setTimeLimitValue(candidate.timeLimit);
    setTimeLeftValue(candidate.timeLeft);
    setConditionsValue(candidate.conditions);

    setReportValue(candidate.reportUrl);
    setOverallValue(candidate.overall);
  }

  const setCandidatesActive = useMutation(
    ({newActive}: any) =>
      postCandidatesSetActive(props.candidateSummary.username,
          props.candidateSummary.candidateR.token,
          props.candidateSummary.candidateR.test,
          props.candidateSummary.candidateL.token,
          props.candidateSummary.candidateL.test,
          props.candidateSummary.candidateS.test,
          props.candidateSummary.candidateW.test,
          newActive),
    {
      onSuccess: (data, variables) => {
        props.candidateSummary.candidateR.active = variables.newActive;
        props.candidateSummary.candidateL.active = variables.newActive;
        props.candidateSummary.candidateS.active = variables.newActive;
        props.candidateSummary.candidateW.active = variables.newActive;
        setAllActive(variables.newActive);

        return queryClient.invalidateQueries(adminKeys.candidateActive());
      },
      onError: () => {
        errorToast(toast, "Error actualizando candidatos");
      },
    });

  const setCandidateActive = useMutation(
    ({publicId, candidateToken, active, type, isAutomated}: any) =>
      postCandidateSetActive(props.candidateSummary.username, publicId, candidateToken, active,
          type, isAutomated),
    {
      onSuccess: (data, variables) => {
        if (variables.type==="reading")
          props.candidateSummary.candidateR.active = variables.active;
        else
        if (variables.type==="speaking")
          props.candidateSummary.candidateS.active = variables.active;
        else
        if (variables.type==="writing")
          props.candidateSummary.candidateW.active = variables.active;
        else
          props.candidateSummary.candidateL.active = variables.active;
        //successToast(toast, "Candidato actualizado");

        setAllActive(checkCandidatesActive());

        return queryClient.invalidateQueries(adminKeys.candidateActive());
      },
      onError: () => {
        errorToast(toast, "Error actualizando candidato");
      },
    });

  const institution = props.candidateSummary.institution !== null ? props.candidateSummary.institution : "";
  const region = props.candidateSummary.region !== null ? props.candidateSummary.region : "";
  const institutionDisplay = institution && region ? institution+" - "+region : institution+region


  function checkCandidatesActive() {
      return (props.candidateSummary.candidateR.status === null ||
                 props.candidateSummary.candidateR.active) &&
             (props.candidateSummary.candidateL.status === null ||
                 props.candidateSummary.candidateL.active) &&
             (props.candidateSummary.candidateS.status === null ||
                 props.candidateSummary.candidateS.active) &&
             (props.candidateSummary.candidateW.status === null ||
                 props.candidateSummary.candidateW.active)
      }
  const [allActive, setAllActive] = useState<boolean>( checkCandidatesActive() );

  return (
    <>
      <Tr key={props.candidateSummary.candidateR.token +" "+ props.candidateSummary.candidateL.token
        +" "+ props.candidateSummary.candidateS.test
        +" "+ props.candidateSummary.candidateW.test}>
        <Td>{props.candidateSummary.username}</Td>
        <Td>{props.candidateSummary.name}</Td>
        <Td>{institutionDisplay}</Td>
        <Td>{props.candidateSummary.userGroup}</Td>
        {  activateCandidates &&
            <Td>
              <Button onClick={()=>setCandidatesActive.mutate({
                newActive: !allActive
              })} colorScheme={allActive ? 'red' : 'green'}
                    isLoading={setCandidatesActive.isLoading} >
                {allActive ? 'Deshabilitar Todos' : 'Habilitar Todos'}
              </Button>
            </Td>
        }

        { !isPublic && <>
            <Td> <DownloadCandidateCertificate username={props.candidateSummary.username}
                                                                   name={props.candidateSummary.name}
                                                                   readingToken={props.candidateSummary.candidateR.token}
                                                                   listeningToken={props.candidateSummary.candidateL.token}
                                                                   speechUrl={props.candidateSummary.candidateS.test}/>
                                                                    </Td>

        </>}
        <Td>{displayCandidateStatus(props.candidateSummary.candidateR.status)} </Td>
        <Td>{displayTime(props.candidateSummary.candidateR.startTime)}</Td>
        <Td>{displayTime(props.candidateSummary.candidateR.endTime)}</Td>
        <Td>{props.candidateSummary.candidateR.lastAnsweredQuestion}</Td>
        { !isPublic && <Td>{props.candidateSummary.candidateR.level}</Td>}
        { !isAdmin && activateCandidates &&
            <Td>
              {props.candidateSummary.candidateR.status !== CandidateStatus.TEST_COMPLETE &&
                props.candidateSummary.candidateR.status !== null &&
                props.candidateSummary.candidateR.active &&
                <Button onClick={()=>setCandidateActive.mutate({
                  publicId: props.candidateSummary.candidateR.test,
                  candidateToken: props.candidateSummary.candidateR.token,
                  active: false, type:"reading", isAutomated:false
                })} colorScheme='red'
                        isLoading={setCandidateActive.isLoading} >
                  Deshabilitar
                </Button>
              } {props.candidateSummary.candidateR.status !== CandidateStatus.TEST_COMPLETE &&
              props.candidateSummary.candidateR.status !== null &&
              !props.candidateSummary.candidateR.active &&
              <Button onClick={()=>setCandidateActive.mutate({
                publicId: props.candidateSummary.candidateR.test,
                candidateToken: props.candidateSummary.candidateR.token,
                active: true, type:"reading", isAutomated:false
              })} colorScheme='green'
                      isLoading={setCandidateActive.isLoading} >
                Habilitar
              </Button>
              }
            </Td>

            }
        { isAdmin &&
          <Td>
            { props.candidateSummary.candidateR.status !== null &&
              <IconButton
              variant="outline"
              colorScheme="gray"
              aria-label="Editar Candidato"
              size="md"
              icon={<EditIcon/>}
              marginX="auto"
              onClick={() => {
                setCandidateValues(props.candidateSummary.candidateR);
                onOpen();
              }}/>}
          </Td>
        }
        <Td>{displayCandidateStatus(props.candidateSummary.candidateL.status)}
              </Td>
        <Td>{displayTime(props.candidateSummary.candidateL.startTime)}</Td>
        <Td>{displayTime(props.candidateSummary.candidateL.endTime)}</Td>
        <Td>{props.candidateSummary.candidateL.lastAnsweredQuestion}</Td>
        { !isPublic && <Td>{props.candidateSummary.candidateL.level}</Td>}
        { !isAdmin && activateCandidates &&
            <Td>
              {props.candidateSummary.candidateL.status !== CandidateStatus.TEST_COMPLETE &&
                props.candidateSummary.candidateL.status !== null &&
                props.candidateSummary.candidateL.active &&
                <Button onClick={()=>setCandidateActive.mutate({
                  publicId: props.candidateSummary.candidateL.test,
                  candidateToken: props.candidateSummary.candidateL.token,
                  active: false, type:"listening", isAutomated:false
                })} colorScheme='red'
                        isLoading={setCandidateActive.isLoading} >
                  Deshabilitar
                </Button>
              } {props.candidateSummary.candidateL.status !== CandidateStatus.TEST_COMPLETE &&
              props.candidateSummary.candidateL.status !== null &&
              !props.candidateSummary.candidateL.active &&
              <Button onClick={()=>setCandidateActive.mutate({
                publicId: props.candidateSummary.candidateL.test,
                candidateToken: props.candidateSummary.candidateL.token,
                active: true, type:"listening", isAutomated:false
              })} colorScheme='green'
                      isLoading={setCandidateActive.isLoading} >
                Habilitar
              </Button>
            }
            </Td>
            }
        { isAdmin &&
          <Td>
            { props.candidateSummary.candidateL.status !== null &&
              <IconButton
              variant="outline"
              colorScheme="gray"
              aria-label="Editar Candidato"
              size="md"
              icon={<EditIcon/>}
              marginX="auto"
              onClick={() => {
                setCandidateValues(props.candidateSummary.candidateL);
                onOpen();
              }}/>}
          </Td>
        }
        <Td>{displayCandidateStatus(props.candidateSummary.candidateS.status)}</Td>
        <Td>{displayTime(props.candidateSummary.candidateS.startTime)}</Td>
        <Td>{displayTime(props.candidateSummary.candidateS.endTime)}</Td>
        { !isPublic && <Td>{props.candidateSummary.candidateS.level}</Td>}
        { isAdmin && <Td>{props.candidateSummary.candidateS.reportUrl}</Td>}
        { !isAdmin && activateCandidates &&
            <Td>
              {props.candidateSummary.candidateS.status !== CandidateStatus.TEST_COMPLETE &&
                props.candidateSummary.candidateS.status !== null &&
                props.candidateSummary.candidateS.active &&
                <Button onClick={()=>setCandidateActive.mutate({
                  publicId: props.candidateSummary.candidateS.test,
                  candidateToken: props.candidateSummary.candidateS.test,
                  active: false, type:"speaking", isAutomated:true
                })} colorScheme='red'
                        isLoading={setCandidateActive.isLoading} >
                  Deshabilitar
                </Button>
              } {props.candidateSummary.candidateS.status !== CandidateStatus.TEST_COMPLETE &&
              props.candidateSummary.candidateS.status !== null &&
              !props.candidateSummary.candidateS.active &&
              <Button onClick={()=>setCandidateActive.mutate({
                publicId: props.candidateSummary.candidateS.test,
                candidateToken: props.candidateSummary.candidateS.test,
                active: true, type:"speaking", isAutomated:true
              })} colorScheme='green'
                      isLoading={setCandidateActive.isLoading} >
                Habilitar
              </Button>
            }
            </Td>
            }
        { isAdmin &&
          <Td>
            { isAdmin && props.candidateSummary.candidateS.status !== null &&
            <IconButton
              variant="outline"
              colorScheme="gray"
              aria-label="Editar Candidato"
              size="md"
              icon={<EditIcon/>}
              marginX="auto"
              onClick={()=>{
                setCandidateValues(props.candidateSummary.candidateS);
                onOpen();
              }} />}
          </Td>
        }


        <Td>{displayCandidateStatus(props.candidateSummary.candidateW.status)}</Td>
        <Td>{displayTime(props.candidateSummary.candidateW.startTime)}</Td>
        <Td>{displayTime(props.candidateSummary.candidateW.endTime)}</Td>
        { !isPublic && <Td>{props.candidateSummary.candidateW.level}</Td>}
        { isAdmin && <Td>{props.candidateSummary.candidateW.reportUrl}</Td>}
        { !isAdmin && activateCandidates &&
            <Td>
              {props.candidateSummary.candidateW.status !== CandidateStatus.TEST_COMPLETE &&
                props.candidateSummary.candidateW.status !== null &&
                props.candidateSummary.candidateW.active &&
                <Button onClick={()=>setCandidateActive.mutate({
                  publicId: props.candidateSummary.candidateW.test,
                  candidateToken: props.candidateSummary.candidateW.test,
                  active: false, type:"writing", isAutomated:true
                })} colorScheme='red'
                        isLoading={setCandidateActive.isLoading} >
                  Deshabilitar
                </Button>
              } {props.candidateSummary.candidateW.status !== CandidateStatus.TEST_COMPLETE &&
              props.candidateSummary.candidateW.status !== null &&
              !props.candidateSummary.candidateW.active &&
              <Button onClick={()=>setCandidateActive.mutate({
                publicId: props.candidateSummary.candidateW.test,
                candidateToken: props.candidateSummary.candidateW.test,
                active: true, type:"writing", isAutomated:true
              })} colorScheme='green'
                      isLoading={setCandidateActive.isLoading} >
                Habilitar
              </Button>
            }
            </Td>
            }
        { isAdmin &&
          <Td>
            { isAdmin && props.candidateSummary.candidateW.status !== null &&
            <IconButton
              variant="outline"
              colorScheme="gray"
              aria-label="Editar Candidato"
              size="md"
              icon={<EditIcon/>}
              marginX="auto"
              onClick={()=>{
                setCandidateValues(props.candidateSummary.candidateW);
                onOpen();
              }} />}
          </Td>
        }

        { isAdmin && <Td>{props.candidateSummary.totalScore}</Td> }
      </Tr>

      <Modal blockScrollOnMount={false} isOpen={isOpen} onClose={onClose} size={"4xl"}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Editar candidato</ModalHeader>
          <ModalBody>
            <HStack spacing={stackSpace} paddingBottom={stackPad}>
              <Text>Prueba</Text>
              <Textarea value={testValue} minW="10em" resize="none" maxW="50%" maxLength={200}
                        disabled={currentCandidate?.evaluationType!==EvaluationType.SPEAKING &&
                          statusValue!==CandidateStatus.INVITED}
                        onChange={(event)=>setTestValue(event.target.value)}/>

              <Text>Banda</Text>
              <Textarea value={levelValue} minW="10em" resize="none" maxW="auto" maxLength={6}
                        onChange={(event)=>setLevelValue(event.target.value)}/>
            </HStack>

            <HStack spacing={stackSpace} paddingBottom={stackPad}>
              <Text>Estado</Text>
              <RadioGroup onChange={setStatusValue} value={statusValue}>
                <Stack direction='column'>
                  <Radio value={CandidateStatus.INVITED}>{displayCandidateStatus(CandidateStatus.INVITED)}</Radio>
                  <Radio value={CandidateStatus.TEST_IN_PROGRESS}>{displayCandidateStatus(CandidateStatus.TEST_IN_PROGRESS)}</Radio>
                  <Radio value={CandidateStatus.TEST_COMPLETE}>{displayCandidateStatus(CandidateStatus.TEST_COMPLETE)}</Radio>
                </Stack>
              </RadioGroup>

              <Checkbox minW="fit-content"
                        onChange={(e) => {
                          setActiveValue(e.target.checked);
                        }}
                        disabled={statusValue===CandidateStatus.TEST_COMPLETE}
                        defaultChecked={activeValue}>
                Habilitado
              </Checkbox>
            </HStack>

            <HStack spacing={stackSpace} paddingBottom={stackPad}>
              <Text>Habilitado desde:</Text>
              <Input type="datetime-local" id="enabledFrom"
                     value={parseDateTime(enabledFromValue)} maxW="50%"
                     onChange={(e) => {
                       setEnabledFromValue(e.target.value);
                     }}/>
              <Text>Habilitado hasta:</Text>
              <Input type="datetime-local" id="enabledTo"
                     value={parseDateTime(enabledToValue)} maxW="50%"
                     onChange={(e) => {
                       setEnabledToValue(e.target.value);
                     }}/>
            </HStack>

            <HStack spacing={stackSpace} paddingBottom={stackPad}>
              <Text>Hora de inicio:</Text>
              <Input type="datetime-local" id="startTime"
                     value={parseDateTime(startTimeValue)} maxW="50%"
                     onChange={(e) => {
                       setStartTimeValue(e.target.value);
                     }}/>
              <Text>Hora de finalización:</Text>
              <Input type="datetime-local" id="endTime"
                     value={parseDateTime(endTimeValue)} maxW="50%"
                     onChange={(e) => {
                       setEndTimeValue(e.target.value);
                     }}/>
            </HStack>

            { (currentCandidate?.evaluationType===EvaluationType.READING ||
                currentCandidate?.evaluationType===EvaluationType.LISTENING) &&
              <>
                <HStack spacing={stackSpace} paddingBottom={stackPad}>
                  <Text>Cantidad de preguntas</Text>
                  <Input type="text" maxW="auto" value={questionLimitValue}
                         onChange={(e) => {
                           const value = e.target.value.replace(/\D/,'');
                           if(parseInt(value) !== 0 && parseInt(value) <= 200) {
                             setQuestionLimitValue(+value);
                           }
                         }}/>
                  <Text>Mútiples intentos</Text>
                  <Input type="text" maxW="auto" value={multipleAttemptsValue}
                         onChange={(e) => {
                           const value = e.target.value.replace(/\D/,'');
                           if(parseInt(value) !== 0 && parseInt(value) <= 100) {
                             setMultipleAttemptsValue(+value);
                           }
                         }}/>
                </HStack>

                <HStack spacing={stackSpace} paddingBottom={stackPad}>
                  <Text>Tiempo límite (segundos)</Text>
                  <Input type="text" maxW="auto" value={timeLimitValue}
                         onChange={(e) => {
                           const value = e.target.value.replace(/\D/,'');
                           if(parseInt(value) !== 0 && parseInt(value) <= maxTime) {
                             setTimeLimitValue(+value);
                           }
                         }}/>
                  <Text>Tiempo restante (segundos)</Text>
                  <Input type="text" maxW="auto" value={timeLeftValue}
                         onChange={(e) => {
                           const value = e.target.value.replace(/\D/,'');
                           if(parseInt(value) !== 0 && parseInt(value) <= maxTime && parseInt(value) <= timeLimitValue) {
                             setTimeLeftValue(+value);
                           }
                         }}/>
                </HStack>
                <HStack spacing={stackSpace} paddingBottom={stackPad}>
                  <Text>Condiciones</Text>
                  <Textarea value={conditionsValue} minW="10em" resize="none" maxW="50%" maxLength={20}
                            onChange={(event) => setConditionsValue(event.target.value)}/>
                  <Text>Última respuesta: {currentCandidate?.lastAnsweredQuestion}</Text>
                </HStack>
              </>
            }

            { (currentCandidate?.evaluationType===EvaluationType.SPEAKING ||
              currentCandidate?.evaluationType===EvaluationType.WRITING) &&
              <>
                <HStack spacing={stackSpace} paddingBottom={stackPad}>
                  <Text>Overall</Text>
                  <Input type="text" maxW="50%" value={overallValue}
                         onChange={(e) => {
                           const value = e.target.value;
                           if (/^(\d\.?|\d\.\d)?$/.test(value))
                             setOverallValue(value);
                         }}/>
                  <Checkbox minW="fit-content"
                            onChange={(e) => {
                              setMultipleAttemptsValue(e.target.checked ? 1 : 0);
                            }}
                            defaultChecked={multipleAttemptsValue > 0}>
                    Mútiples intentos
                  </Checkbox>
                </HStack>
                <HStack spacing={stackSpace} paddingBottom={stackPad}>
                  <Text>Reporte</Text>
                  <Textarea value={reportValue} minW="10em" resize="none" maxW="50%" maxLength={200}
                            onChange={(event) => setReportValue(event.target.value)}/>
                </HStack>
              </>
            }

          </ModalBody>
          <ModalCloseButton />
          <ModalFooter>
            <Button
              marginX={10}
              colorScheme="teal"
              type="submit"
              onClick={() => handleSaveClick()}
            >
              Guardar
            </Button>
            <Button colorScheme="teal" type="submit" onClick={()=>{
              onClose();
            }} >
              Cancelar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
export default CandidateSummaryItem;